import React from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EsgSection from './sections/EsgSection';
import KpiFigure, { type KpiFigureProps } from '../metrics/KpiFigure';
import MetricStrip from '../metrics/MetricStrip';
import { ESG_ICONS } from '../../constants/esgIcons';
import { HSE_TERMS } from '../../constants/hseTerms';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import type { HseSummary } from '../../types/hse-summary.type';

export interface HseDashboardSectionProps {
  hse: HseSummary | undefined;
  /** Monthly only: drives month-over-month deltas. */
  previous?: HseSummary;
  periodText: string;
  comparisonLabel?: string;
}

type CountKey = 'recordable_injuries' | 'lost_time_injuries' | 'lost_days' | 'fatalities';
type RateKey = 'trir' | 'ltifr';

const COUNT_FIGURES: { key: CountKey; label: string }[] = [
  { key: 'recordable_injuries', label: 'hse.dashboard.recordableInjuries' },
  { key: 'lost_time_injuries', label: 'hse.dashboard.lostTimeInjuries' },
  { key: 'lost_days', label: 'hse.dashboard.lostDays' },
  { key: 'fatalities', label: 'hse.dashboard.fatalities' },
];

const RATE_FIGURES: { key: RateKey; label: string; unit: string; info: string }[] = [
  { key: 'trir', label: 'hse.dashboard.trir', unit: 'hse.dashboard.per200k', info: 'hse.dashboard.trirInfo' },
  { key: 'ltifr', label: 'hse.dashboard.ltifr', unit: 'hse.dashboard.per1m', info: 'hse.dashboard.ltifrInfo' },
];

/**
 * Health & safety on the ESG dashboard (GRI 403-9): headline injury counts, the
 * injury rates where hours worked were reported, and incidents by type.
 */
const HseDashboardSection: React.FC<HseDashboardSectionProps> = ({ hse, previous, periodText, comparisonLabel }) => {
  const { t } = useTranslation();
  const fmt = useNumberFormat();
  const navigate = useNavigate();

  const header = {
    title: t('dashboard.sections.healthSafety'),
    icon: ESG_ICONS.healthSafety,
    description: t('dashboard.sectionDescriptions.healthSafety'),
  };

  if (!hse || hse.weeks_reported === 0) {
    return (
      <EsgSection {...header}>
        <Paper elevation={0} sx={{ p: 3, borderRadius: '8px', border: '1px dashed', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('hse.dashboard.noData', { period: periodText })}
          </Typography>
          <Button variant="outlined" size="small" onClick={() => navigate('/hse')}>
            {t('hse.dashboard.openHse')}
          </Button>
        </Paper>
      </EsgSection>
    );
  }

  const countDelta = (key: CountKey): KpiFigureProps['delta'] => {
    const prev = previous?.[key];
    if (!previous || !prev) return undefined;
    const change = ((hse[key] - prev) / prev) * 100;
    if (!change) return undefined;
    return { value: change, kind: 'relative', polarity: 'lower_is_better', comparisonLabel };
  };

  const countFigures: KpiFigureProps[] = COUNT_FIGURES.map(({ key, label }) => {
    const f = fmt.metric('count', hse[key]);
    return {
      label: t(label),
      icon: ESG_ICONS.healthSafety,
      value: f.text,
      context: periodText,
      delta: countDelta(key),
    };
  });

  const rateContext =
    hse.weeks_with_hours > 0
      ? t('hse.dashboard.ratesBasis', { weeks: fmt.number(hse.weeks_with_hours), reported: fmt.number(hse.weeks_reported) })
      : t('hse.dashboard.noHours');
  const rateFigures: KpiFigureProps[] = RATE_FIGURES.map(({ key, label, unit, info }) => {
    const value = hse[key];
    return {
      label: t(label),
      icon: ESG_ICONS.healthSafety,
      value: value === null ? '—' : fmt.number(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      unit: value === null ? undefined : t(unit),
      context: rateContext,
      info: t(info),
    };
  });

  const meta = [
    t('hse.dashboard.weeksSubmitted', { submitted: fmt.number(hse.weeks_submitted), reported: fmt.number(hse.weeks_reported) }),
    hse.hours_worked !== null ? t('hse.dashboard.hoursWorked', { hours: fmt.number(hse.hours_worked) }) : null,
    hse.headcount !== null ? t('hse.dashboard.headcount', { count: fmt.number(hse.headcount) }) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <EsgSection {...header} meta={meta}>
      <Box sx={{ display: 'grid', gap: { xs: 2, md: 2.5 } }}>
        <MetricStrip columns={{ xs: 2, sm: 2, md: 4 }}>
          {countFigures.map((props, i) => (
            <KpiFigure key={COUNT_FIGURES[i].key} {...props} />
          ))}
        </MetricStrip>
        <MetricStrip columns={{ xs: 1, sm: 2, md: 2 }}>
          {rateFigures.map((props, i) => (
            <KpiFigure key={RATE_FIGURES[i].key} {...props} />
          ))}
        </MetricStrip>
        <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: '8px', border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
            {t('hse.dashboard.byType', { period: periodText })}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
              columnGap: 3,
              rowGap: 1,
            }}
          >
            {HSE_TERMS.map((term) => {
              const count = hse.counts[term.key] ?? 0;
              return (
                <Box
                  key={term.key}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 2,
                    py: 0.75,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body2" color={count ? 'text.primary' : 'text.secondary'} noWrap>
                    {t(`hse.terms.${term.key}.label`)}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: count ? 700 : 400, fontVariantNumeric: 'tabular-nums' }}>
                    {fmt.number(count)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Paper>
      </Box>
    </EsgSection>
  );
};

export default HseDashboardSection;
